import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, lastValueFrom, map, Observable } from 'rxjs';

import { UrlBuilder } from '@app/shared/helpers/url-builder';
import { PaginatedResult } from '@app/shared/models';
import { DashbaordListeAttenteItem, DashboardDspStats, DashboardListeAttenteStats, DashboardOccupationItem, DashboardOccupationStats, DashboardQuery } from '@app/shared/models/dashboard';
import { environment } from "@env/environment";

@Injectable()
export class DashboardService {
  private readonly http: HttpClient = inject(HttpClient);

  public getOccupationDashboard(query: DashboardQuery): Observable<PaginatedResult<DashboardOccupationItem>> {
    const url: string = UrlBuilder.create(environment.apiUrl)
      .addQuerySegments('v1', 'clients', 'dashboard', 'occupation')
      .build();

    return this.http.post<PaginatedResult<DashboardOccupationItem>>(url, query);
  }

  public getOccupationDashboardStats(query: DashboardQuery): Promise<DashboardOccupationStats> {
    const url: string = UrlBuilder.create(environment.apiUrl)
      .addQuerySegments('v1', 'clients', 'dashboard', 'occupation', 'stats')
      .build();

    return firstValueFrom(this.http.post<DashboardOccupationStats>(url, query));
  }

  public downloadOccupationDashboardExport(query: DashboardQuery): Promise<Blob | null> {
    const url: string = UrlBuilder.create(environment.apiUrl)
      .addQuerySegments('v1', 'clients', 'dashboard', 'occupation', 'export')
      .build();
    const obs$: Observable<Blob | null> = this.downloadFile(url, query);

    return lastValueFrom(obs$);
  }

  public getListeAttenteDashboard(query: DashboardQuery): Promise<PaginatedResult<DashbaordListeAttenteItem>> {
    const url: string = UrlBuilder.create(environment.apiUrl)
      .addQuerySegments('v1', 'clients', 'dashboard', 'liste-attente')
      .build();

    return firstValueFrom(this.http.post<PaginatedResult<DashbaordListeAttenteItem>>(url, query));
  }

  public getListeAttenteDashboardStats(query: DashboardQuery): Promise<DashboardListeAttenteStats> {
    const url: string = UrlBuilder.create(environment.apiUrl)
      .addQuerySegments('v1', 'clients', 'dashboard', 'liste-attente', 'stats')
      .build();

    return firstValueFrom(this.http.post<DashboardListeAttenteStats>(url, query));
  }

  public downloadListeAttenteDashboardExport(query: DashboardQuery): Promise<Blob | null> {
    const url: string = UrlBuilder.create(environment.apiUrl)
      .addQuerySegments('v1', 'clients', 'dashboard', 'liste-attente', 'export')
      .build();

    const obs$: Observable<Blob | null> = this.downloadFile(url, query);

    return lastValueFrom(obs$);
  }

  public getDspDashboardStats(query: DashboardQuery): Promise<DashboardDspStats> {
    const url: string = UrlBuilder.create(environment.apiUrl)
      .addQuerySegments('v1', 'clients', 'dashboard', 'dsp', 'stats')
      .build();

    return firstValueFrom(this.http.post<DashboardDspStats>(url, query));
  }

  private downloadFile(url: string, query: DashboardQuery): Observable<Blob | null> {
    return this.http.post(url, query, {
      reportProgress: true,
      responseType: 'blob',
      observe: 'events'
    }).pipe(
      map((result: HttpEvent<Blob>) => {
        return result.type === HttpEventType.Response ? result.body : null;
      })
    );
  }
}
